import type {MapboxOptions} from 'mapbox-gl';
import getBoundingBox, {isBoundingBoxLargerThanGlobe} from 'src/utils/map/getBoundingBox';
import {getPlaceCameraPosition} from './useMapUtils';
import getCenter from 'src/utils/map/getCenter';

const lerp = (x: number, y: number, a: number) => x * (1 - a) + y * a;

const clamp = (a: number, min = 0, max = 1) => Math.min(max, Math.max(min, a));

const invlerp = (x: number, y: number, a: number) => clamp((a - x) / (y - x));

const maxSize = 1200;
const minSize = 230;

const maxZoom = 2.5;
const minZoom = 0.8;

export const getGlobeZoom = (size: number): number => {
  return lerp(minZoom, maxZoom, invlerp(minSize, maxSize, size));
};

const getInitialMapCamera = (
  places: Partial<Place>[],
  size: number,
  variant: 'countries' | 'places',
  zoomOverride?: number
): Partial<MapboxOptions> => {
  if (variant === 'countries') return {zoom: zoomOverride || getGlobeZoom(size), center: getCenter(places)};

  const params = new URLSearchParams(document.location.search);

  const selectedPlaceQuery = params.get('selectedPlace');
  const selectedPlace =
    selectedPlaceQuery && places.find((place) => `${place.nameId}_${place.countryCode}` === selectedPlaceQuery);

  const singlePlace = selectedPlace || (places.length === 1 ? places[0] : undefined);

  if (singlePlace) {
    const cameraPosition = getPlaceCameraPosition(singlePlace, zoomOverride);

    return cameraPosition || {zoom: 2};
  }

  if (!places.length) return {zoom: zoomOverride || 2};

  const coordinates = places.map((p) => p.location?.coordinates).filter(Boolean);
  const boundingBox = getBoundingBox(coordinates);
  const isBig = isBoundingBoxLargerThanGlobe(boundingBox);

  if (isBig) return {zoom: zoomOverride || getGlobeZoom(size), center: getCenter(places)};

  return {bounds: boundingBox, fitBoundsOptions: {padding: 40}};
};

export default getInitialMapCamera;
